/* eslint-disable class-methods-use-this */
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { HelpIcon } from '@THC/components/Icons/Help';
import T from 'i18n-react';
import Link from 'next/link';

import fees from '@motorway/motorway-sale-rules/lib/fees';
import { ErrorIcon, InfoFilledIcon, ToastContext } from '@motorway/motorway-storybook-cra';
import { Button, DotList } from '@motorway/mw-highway-code';
import { VRM } from '@motorway/mw-highway-code/alpha';
import { toast } from '@motorway/mw-highway-code/pre-alpha';

import cypressIds from 'Components/cypress_ids.json';
import { IconTooltip } from 'Components/shared/IconTooltip';
import TimeCountdown from 'Components/shared/TimeCountdown/TimeCountdown';
import { withFeaturesContext } from 'Context/FeaturesContext/FeaturesContext';
import { VEHICLE_PURCHASE_MODAL_BID_TYPES, VEHICLE_PURCHASE_MODAL_TYPES } from 'Context/VehicleInfoContext';
import { VEHICLE_PURCHASE_TYPES } from 'Controllers/VehiclePermitsChecker/VehiclePermitsChecker';
import { WithVehiclePermitsChecker } from 'Controllers/VehiclePermitsChecker/VehiclePermitsChecker.helpers';
import {
	triggerBidAccepted,
	triggerConfirmBuyItNowCTAClickedEvent,
	triggerConfirmMaxBidTooltipClickedEvent,
	triggerIncreaseOfferConfirmationCTAClickedEvent,
	triggerMaxFeeTooltipClickedEvent,
	triggerSubmitBidConfirmationCTAClickedEvent,
	triggerSubmitOfferConfirmationCTAClickedEvent,
	triggerTermsLinkClicked,
} from 'Services/analytics/events';
import { createBid } from 'Services/api/bids';
import { sendCreateBidBrazeEvent } from 'Services/braze/brazeEvents.helpers';
import { logger } from 'Services/logger/logger';
import useFeesStore from 'Stores/FeesStore/FeesStore';
import { BID_TYPES } from 'Utilities/biddingState';
import { formatBidValueToNumber, formatText, formatVehicleName } from 'Utilities/formatters';
import FormBase from 'Utilities/FormBase/FormBase';
import { INTENT_TYPES } from 'Utilities/FormBase/FormBase.helpers';
import { applyCypressData, isEmpty, numberWithCommas } from 'Utilities/helpers';
import { getMobilizeFeeOptions } from 'Utilities/helpers/getMobilizeFeeOptions';
import proPropTypes from 'Utilities/proPropTypes';
import { FOR_SALE_CATEGORY } from 'Utilities/vehicleState';

import { FEEDBACK_TOOLTIP_TIMEOUT, SUBMISSION_TYPE, TOAST_MESSAGE } from './VehiclePurchaseModalContent.consts';

import styles from '../PurchaseModal.module.scss';

T.setTexts(require('Components/texts.json'));

export const handleBidOrOfferSubmitAnalyticsVDPRedesign = ({
	amount,
	enquiryId,
	errorMessage,
	modalType,
	success,
	vrm,
}) => {
	switch (modalType) {
		case VEHICLE_PURCHASE_MODAL_TYPES.PLACE_BID_FROM_SIDEBAR:
		case VEHICLE_PURCHASE_MODAL_TYPES.PLACE_BID_FROM_FOOTER:
			triggerSubmitBidConfirmationCTAClickedEvent({ bidAmount: amount, enquiryId, errorMessage, success, vrm });
			return;
		case VEHICLE_PURCHASE_MODAL_TYPES.BUY_IT_NOW:
			triggerConfirmBuyItNowCTAClickedEvent({ errorMessage, offerAmount: amount, success });
			return;
		case VEHICLE_PURCHASE_MODAL_TYPES.MAKE_OFFER:
			triggerSubmitOfferConfirmationCTAClickedEvent({ errorMessage, offerAmount: amount, success });
			return;
		case VEHICLE_PURCHASE_MODAL_TYPES.INCREASE_OFFER:
			triggerIncreaseOfferConfirmationCTAClickedEvent({ errorMessage, offerAmount: amount, success });
			break;
		default:
			break;
	}
};

class VehiclePurchaseModalContent extends FormBase {
	constructor(props) {
		super(props);

		const { modalType, permits, value: placeBidValue } = props;
		const { type } = permits.getVehiclePurchaseType();

		this.$form = React.createRef();
		this.textsPrefix = 'vehicleDetails.bidsModal';
		this.inputName = type === VEHICLE_PURCHASE_TYPES.SALE ? 'offer' : 'bid';

		this.INITIAL_STATE = {
			feedbackTooltip: {},
			inputName: this.inputName,
			inputs: [
				{
					focused: false,
					name: this.inputName,
					required: true,
					showIfInvalid: false,
					valid: true,
					value: '',
				},
			],
			loading: false,
			mtFee: 0,
			saleEnded: false,
			status: null,
		};

		this.state = this.INITIAL_STATE;

		const { value } = permits.getDisplayPrice();

		this.makeOffer =
			permits.hasVehicleState(FOR_SALE_CATEGORY.FOR_SALE) && modalType === VEHICLE_PURCHASE_MODAL_TYPES.BUY_IT_NOW;

		this.placeBid =
			permits.hasVehicleState(FOR_SALE_CATEGORY.SALE_ACTIVE) &&
			modalType === VEHICLE_PURCHASE_MODAL_TYPES.PLACE_BID_FROM_SIDEBAR;

		this.offerValue = placeBidValue ? formatBidValueToNumber(placeBidValue) : value;

		// When we open Buy in now / add bid modal, we need to set value to form upfront as we don't have input :)
		if (this.makeOffer || this.placeBid) {
			this.state = Object.assign(this.state, {
				// note: if in future this component will have more that 1 input,
				// this merge need to be updated
				inputs: [
					{
						...this.state.inputs.find((input) => input.name === this.inputName),
						value: numberWithCommas(this.offerValue),
					},
				],
			});
		}
	}

	get dialogCopyByModalType() {
		const { modalType } = this.props;

		return (
			{
				[VEHICLE_PURCHASE_MODAL_TYPES.PLACE_BID_FROM_SIDEBAR]: {
					submission: SUBMISSION_TYPE.bid,
					toastMessage: TOAST_MESSAGE.bid,
				},
				[VEHICLE_PURCHASE_MODAL_TYPES.PLACE_BID_FROM_FOOTER]: {
					submission: SUBMISSION_TYPE.bid,
					toastMessage: TOAST_MESSAGE.bid,
				},
				[VEHICLE_PURCHASE_MODAL_TYPES.MAKE_OFFER]: {
					submission: SUBMISSION_TYPE.offer,
					toastMessage: TOAST_MESSAGE.offer,
				},
				[VEHICLE_PURCHASE_MODAL_TYPES.INCREASE_OFFER]: {
					submission: SUBMISSION_TYPE.offer,
					toastMessage: TOAST_MESSAGE.offer,
				},
				[VEHICLE_PURCHASE_MODAL_TYPES.BUY_IT_NOW]: {
					submission: SUBMISSION_TYPE.bin,
					toastMessage: TOAST_MESSAGE.bin,
				},
			}[modalType] || {}
		);
	}

	get purchaseType() {
		const { modalType } = this.props;

		return VEHICLE_PURCHASE_MODAL_BID_TYPES[modalType] || null;
	}

	static getDerivedStateFromProps(props, state) {
		const {
			vehicle: { hasOtherContactActiveBid },
		} = props;
		const { inputName } = state;

		if (hasOtherContactActiveBid) {
			return {
				hasOtherContactActiveBid: true,
				// note: if in future this component will have more that 1 input,
				// this merge need to be updated
				inputs: [
					{
						...state.inputs.find((input) => input.name === inputName),
						showIfInvalid: true,
						valid: false,
					},
				],

				saleEnded: true,
			};
		}

		if (state.hasOtherContactActiveBid && !hasOtherContactActiveBid) {
			return {
				hasOtherContactActiveBid: false,
				saleEnded: false,
			};
		}

		return null;
	}

	componentDidUpdate(prevProps, { cacheKey, inputFocused }) {
		const { isMobile, modalType, permits } = this.props;
		const { cacheKey: newCacheKey, inputFocused: newInputFocused, saleEnded } = this.state;

		const { type } = permits.getVehiclePurchaseType();

		const closeModal =
			!type || // close popup if vehicle is moved outside sale states // close popup if vehicle is in sale active, user have BIN modal opened, but other contact bid
			(saleEnded && type === VEHICLE_PURCHASE_TYPES.SALE && modalType === VEHICLE_PURCHASE_MODAL_TYPES.BUY_IT_NOW);

		if (closeModal) {
			this.props.onDismissModal();
		}

		if (cacheKey === newCacheKey && inputFocused === newInputFocused) {
			return;
		}

		clearTimeout(this.feedbackTooltipTimer);

		this.feedbackTooltipTimer = setTimeout(() => {
			this.setState({
				feedbackTooltip: {
					content: this.customErrorMessage(this.inputName),
					fillWidth: isMobile,
					placement: 'bottom',
					tooltipIcon: this.getFeedbackIcon(),
				},
			});
		}, FEEDBACK_TOOLTIP_TIMEOUT);
	}

	componentWillUnmount = () => clearTimeout(this.feedbackTooltipTimer);

	getErrorMessage = () => null; // Overwrite parent class' function

	getFeeOptions = () => {
		const { userPartners, vehicle } = this.props;
		return getMobilizeFeeOptions({
			partners: userPartners,
			sourcePartner: vehicle?.sourcePartner,
		});
	};

	isErrorIntent = (id, mustFocus = true) => {
		// Overwrite parent class' function
		const { checkValidity, getInputProp, state } = this;
		const { inputFocused } = state;

		return !checkValidity(id) && getInputProp(id, 'showIfInvalid') && (mustFocus ? !inputFocused : true);
	};

	async componentDidMount() {
		const { onMount } = this.props;
		onMount?.();

		if (this.makeOffer || this.placeBid) {
			const feeOptions = this.getFeeOptions();
			this.setState({
				mtFee: fees.calculateFee(this.offerValue, undefined, feeOptions),
			});
		}
	}

	validateRequiredCriteria = (value) => this.isValidNumber(value) && !this.isBelowMinimum(value);

	validateWarningCriteria = (value) => this.isValidNumber(value) && !this.isBetweenMinimumAndReserve(value);

	isValidNumber = (value) => {
		const number = formatBidValueToNumber(value);
		return !Number.isNaN(number);
	};

	isBelowMinimum = (value) => {
		const {
			vehicle: { minimumBid },
		} = this.props;
		return formatBidValueToNumber(value) < minimumBid.value;
	};

	isBetweenMinimumAndReserve = (value) => {
		const {
			vehicle: { sellerWillAcceptPrice },
		} = this.props;
		const formattedValue = formatBidValueToNumber(value);

		return formattedValue < sellerWillAcceptPrice && !this.isBelowMinimum(value);
	};

	isValueAndReserveLengthMatch = (value) => {
		const {
			vehicle: { sellerWillAcceptPrice },
		} = this.props;

		if (!sellerWillAcceptPrice) {
			return false;
		}

		const valueAsNumber = formatBidValueToNumber(value);
		return valueAsNumber.toString().length === sellerWillAcceptPrice.toString().length;
	};

	customOnFocus = async () =>
		this.setState({
			inputFocused: true,
		});

	customOnBlur = async (id) => {
		this.setState({
			inputFocused: false,
		});

		const value = this.getValue(id);
		return this.validateRequiredCriteria(value);
	};

	customShowIfInvalid = ({ id, type }) => {
		if (this.purchaseType === BID_TYPES.OFFER) {
			return false;
		}

		switch (type) {
			case 'keyup': {
				const value = this.getValue(id);
				if (
					this.validateRequiredCriteria(value) &&
					(this.isValueAndReserveLengthMatch(value) || this.isBetweenMinimumAndReserve(value))
				) {
					return true;
				}

				break;
			}
			default: {
				break;
			}
		}

		return null;
	};

	customInputIntent = (id) => {
		const value = this.getValue(id);

		if (this.isSuccessIntent(id)) {
			return INTENT_TYPES.SUCCESS;
		}

		if (
			this.validateRequiredCriteria(value) &&
			this.isBetweenMinimumAndReserve(value) &&
			this.purchaseType === BID_TYPES.PROXY
		) {
			return INTENT_TYPES.WARNING;
		}

		if (this.isErrorIntent(id)) {
			return INTENT_TYPES.ERROR;
		}

		return null;
	};

	isValidValue = (value) => /[1-9]+(?:,\d+)*/.test(value);

	customInputFormat = (id, value) => {
		if (this.isValidValue(value)) {
			return numberWithCommas(value);
		}
		return '';
	};

	customValidation = (set) => {
		set(this.inputName, { valid: this.validateRequiredCriteria(this.getValue(this.inputName)) });
	};

	customOnChange = async (id, value) => {
		const bidValue = formatBidValueToNumber(value);
		const feeOptions = this.getFeeOptions();
		const mtFee = fees.calculateFee(bidValue, undefined, feeOptions);

		this.setState(() => ({
			cacheKey: value,
			mtFee,
			status: null,
		}));

		if (this.purchaseType === BID_TYPES.OFFER) {
			return false;
		}

		return this.validateRequiredCriteria(value) && this.validateWarningCriteria(value);
	};

	customErrorMessage = (id) => {
		const { inputFocused, status } = this.state;
		const { permits, vehicle } = this.props;
		const { hasOtherContactActiveBid, minimumBid, sellerWillAcceptPrice } = vehicle;
		const { type } = permits.getVehiclePurchaseType();
		const value = this.getValue(id);

		if (hasOtherContactActiveBid && id === this.inputName) {
			const base = 'vehicleDetails.bidding.sale.informations.unavailable';
			const message = T.translate(`${base}.text`, {
				what: T.translate(`${base}.what.${type}`),
			});
			handleBidOrOfferSubmitAnalyticsVDPRedesign({
				errorMessage: message,
				modalType: this.props.modalType,
				success: false,
			});

			return <span>{message}</span>;
		}

		if (inputFocused) {
			if (this.isBetweenMinimumAndReserve(value)) {
				return (
					<span>
						<b>{`${T.translate(`${this.textsPrefix}.warningMessages.type.important`)}: `}</b>
						{T.translate(`${this.textsPrefix}.warningMessages.belowReserve`, {
							difference: numberWithCommas(sellerWillAcceptPrice - formatBidValueToNumber(value)),
						})}
					</span>
				);
			}
		} else if (this.isBelowMinimum(value)) {
			const message = T.translate(`${this.textsPrefix}.errorMessages.minimum:${minimumBid.type}`, {
				minimum: numberWithCommas(minimumBid.value),
			});
			handleBidOrOfferSubmitAnalyticsVDPRedesign({
				errorMessage: message,
				modalType: this.props.modalType,
				success: false,
			});

			return <>{message}</>;
		}

		return status ? <span>{status}</span> : null;
	};

	submit = async () => {
		const successfulBid = await this.createBid();
		if (successfulBid) {
			this.props.onSubmit();
		}
	};

	showToast = (err) => {
		const { status } = this.state;

		const toastStatus = err ? INTENT_TYPES.ERROR : INTENT_TYPES.SUCCESS;
		const msg =
			err && status
				? status
				: T.translate(`vehicleDetails.bidsModal.${this.dialogCopyByModalType.toastMessage}`, {
						value: `£${formatText(this.getValue(this.inputName))}`,
					});

		toast({ content: msg, variant: toastStatus });
	};

	createBid = async () => {
		const {
			isBrazeSDKInitialised,
			vehicle: { id, sourcePartner },
		} = this.props;

		this.setState({
			sending: true,
		});

		const value = this.getValue(this.inputName);

		const errorState = (error = {}) => {
			const scope = 'VehiclePurchaseModalContent createBid';
			const message = error.response?.data?.message || error?.message || 'There was an issue with creating a bid';

			this.setState(
				{
					sending: false,
					status: message,
				},
				() => this.showToast(true),
			);

			handleBidOrOfferSubmitAnalyticsVDPRedesign({
				errorMessage: message,
				modalType: this.props.modalType,
				success: false,
			});

			this.updateInputState(this.inputName, {
				valid: false,
			});

			if (isEmpty(error)) {
				logger.warn({ message: 'Empty error object passed to errorState', scope });
			} else {
				const context = { extra: { info: 'Bid has failed', response: error.response?.data } };
				logger.error({ context, error, message, scope });
			}

			return false;
		};

		const successState = (bid) => {
			this.showToast();

			const { enquiryId, state, type, value: amount } = bid?.data || {};
			handleBidOrOfferSubmitAnalyticsVDPRedesign({
				amount,
				enquiryId,
				modalType: this.props.modalType,
				success: true,
				vrm: this.props.vehicle?.prettyVrm,
			});

			if (isBrazeSDKInitialised) {
				sendCreateBidBrazeEvent({ bidState: state, bidType: type, enquiryId });
			}

			this.setState({
				sending: false,
			});

			return true;
		};

		const bid = await createBid({
			enquiryId: id,
			sourcePartner,
			type: this.purchaseType,
			value: formatBidValueToNumber(value),
		}).catch((err) => err);

		if (bid?.data?.id) {
			triggerBidAccepted({
				id: bid?.data?.id,
			});
			return successState(bid);
		}

		return errorState(bid);
	};

	onSaleEnded = () => {
		this.setState({
			saleEnded: true,
		});
	};

	resolveModalDetails = () => {
		const { modalType } = this.props;

		const forSaleVehicleProps = {
			showBidsAndCountdown: false,
		};

		if (modalType === VEHICLE_PURCHASE_MODAL_TYPES.BUY_IT_NOW) {
			return {
				...forSaleVehicleProps,
				displayPrice: false,
				modalTitle: T.translate('vehicleDetails.bidding.buyItNow'),
				showBiddingInput: false,
				showOfferPreview: true,
			};
		}

		if (modalType === VEHICLE_PURCHASE_MODAL_TYPES.MAKE_OFFER) {
			return {
				...forSaleVehicleProps,
				displayPrice: true,
				modalTitle: T.translate('vehicleDetails.bidding.makeOffer'),
				showBiddingInput: true,
				showOfferPreview: false,
			};
		}

		if (modalType === VEHICLE_PURCHASE_MODAL_TYPES.INCREASE_OFFER) {
			return {
				...forSaleVehicleProps,
				displayPrice: true,
				modalTitle: T.translate('vehicleDetails.bidding.increaseOffer'),
				showBiddingInput: true,
				showOfferPreview: false,
			};
		}

		if (modalType === VEHICLE_PURCHASE_MODAL_TYPES.PLACE_BID_FROM_FOOTER) {
			return {
				displayPrice: true,
				modalTitle: T.translate('vehicleDetails.bidsModal.title'),
				showBiddingInput: true,
				showBidsAndCountdown: true,
				showOfferPreview: false,
			};
		}

		return {
			// VEHICLE_PURCHASE_MODAL_TYPES.PLACE_BID_FROM_SIDEBAR - Live sale in progress/sale active
			...forSaleVehicleProps,
			displayPrice: true,
			modalTitle: T.translate('vehicleDetails.bidding.confirmBid'),
			showBiddingInput: false,
			showOfferPreview: true,
		};
	};

	getProxyBiddingExplanation = ({ explanation = '', onClickOrHover }) => {
		if (this.purchaseType === BID_TYPES.OFFER) {
			return null;
		}

		return (
			<IconTooltip
				content={explanation}
				icon={HelpIcon}
				onClick={onClickOrHover}
				onMouseEnter={onClickOrHover}
				side="top"
			/>
		);
	};

	getBidFee = () => (
		<div className={styles.fee}>
			<span>
				{T.translate(`vehicleDetails.bidsModal.fee:${this.purchaseType}`, { price: formatText(this.state.mtFee || 0) })}
			</span>
			{this.getProxyBiddingExplanation({
				explanation: T.translate('vehicleDetails.bidsModal.maxFeeExplanation'),
				onClickOrHover: () => triggerMaxFeeTooltipClickedEvent(true),
			})}
		</div>
	);

	getFeedbackIcon = () => {
		const intentType = this.getInputIntent(this.inputName);

		switch (intentType) {
			case INTENT_TYPES.WARNING: {
				return <InfoFilledIcon className={styles.bidWarningIcon} />;
			}
			default: {
				return undefined;
			}
		}
	};

	getBiddingInput = () => {
		const { vehicle } = this.props;
		const { hasOtherContactActiveBid, isVatQualifying } = vehicle;
		const { feedbackTooltip } = this.state;
		const { showBiddingInput } = this.resolveModalDetails();
		const { MAXIMUM_PURCHASE_VALUE_LENGTH } = useFeesStore.getState();

		if (!showBiddingInput) {
			return null;
		}

		return (
			<>
				<div {...applyCypressData(cypressIds.text.bidsModalPrice)} className={styles.inputBid}>
					{this.getTextInput({
						feedbackTooltip,
						inputProps: {
							autoComplete: 'off',
							...applyCypressData(cypressIds.inputs.makeOffer),
							disabled: hasOtherContactActiveBid,
							inputMode: 'numeric',
							maxLength: MAXIMUM_PURCHASE_VALUE_LENGTH,
							pattern: '[1-9]+(,[0-9]+)*',
						},
						name: this.inputName,
						prefix: '£',
						suffix: isVatQualifying ? ' +VAT' : null,
					})}
				</div>
				{this.getBidFee()}
			</>
		);
	};

	getOfferPreview = () => {
		const { showOfferPreview } = this.resolveModalDetails();

		if (!showOfferPreview) {
			return null;
		}

		const { permits } = this.props;
		const { status } = this.state;
		const { showVAT } = permits.getVehiclePrice();

		return (
			<div className={styles.offerPreview}>
				<hr className={styles.separator} />
				{status ? (
					<div className={styles.offerError}>
						<ErrorIcon />
						{status}
					</div>
				) : (
					<>
						<h3 {...applyCypressData(cypressIds.text.bidsModalPrice)} className={styles.offerValue}>
							{`£${formatText(this.getValue(this.inputName))}`}
							{showVAT ? <div className={styles.vat}> +VAT</div> : null}
						</h3>
						{this.getBidFee()}
					</>
				)}
				<hr className={styles.separator} />
			</div>
		);
	};

	render() {
		const { bidCount, isKeyboardOpen, modalType, permits, saleEnds, vehicle } = this.props;

		const { PROXY_BID_INCREMENT } = useFeesStore.getState();

		const { value: userCurrentBid } = permits.getUserBid() || {};

		const {
			highestBid,
			prettyVrm,
			spec: { make, model },
			vrm,
		} = vehicle;

		const { displayPrice, modalTitle, showBidsAndCountdown } = this.resolveModalDetails();

		const bidLabel = bidCount === 1 ? 'vehicleDetails.bidsModal.numbersBid' : 'vehicleDetails.bidsModal.numbersBids';

		const priceDetails = permits.getDisplayPrice();

		return (
			<form
				ref={this.$form}
				noValidate
				className={cx(styles.bidsContainer, {
					[styles.keyboardOpen]: isKeyboardOpen,
				})}
				onSubmit={this.onSubmit}
			>
				<div className={styles.infoContainer}>
					<div className={styles.title}>
						<h1 className={cx('mw-title-3 text-center-align')}>{modalTitle}</h1>
						{!this.props.breakpoints?.minWidth?.breakpointTablet
							? this.getProxyBiddingExplanation({
									explanation: T.translate('vehicleDetails.bidsModal.proxyBiddingExplanation', {
										value: PROXY_BID_INCREMENT,
									}),
									onClickOrHover: triggerConfirmMaxBidTooltipClickedEvent,
								})
							: null}
					</div>
					<div className={styles.vrm}>
						<VRM size="regular" text={prettyVrm || vrm} />
					</div>
					<h4 className={styles.vehicleName}>{formatVehicleName({ make, model })}</h4>
					<DotList data-thc-dotlist narrow>
						{displayPrice ? (
							<li>
								{T.translate(`vehicleDetails.bidsModal.${priceDetails.label}`, {
									price: formatText(priceDetails.value),
								})}
							</li>
						) : null}
						{showBidsAndCountdown ? (
							<>
								<li>{T.translate(bidLabel, { number: bidCount })}</li>
								<li>
									<TimeCountdown countdownTime={saleEnds} whenCountdownEnds={this.onSaleEnded}>
										{({ timeLeft, timeString }) =>
											timeLeft ? (
												<span>{timeString || T.translate('vehicleDetails.bidding.ended')}</span>
											) : (
												T.translate('vehicleDetails.bidding.ended')
											)
										}
									</TimeCountdown>
								</li>
							</>
						) : null}
						{highestBid && (
							<li>
								{T.translate('vehicleDetails.bidsModal.highestOffer', {
									price: formatText(highestBid),
								})}
							</li>
						)}
						{userCurrentBid && (
							<li>
								{T.translate('vehicleDetails.bidsModal.userCurrentOffer', {
									price: formatText(userCurrentBid),
								})}
							</li>
						)}
					</DotList>
					{this.getBiddingInput()}
					{this.getOfferPreview()}
				</div>
				<div className={styles.footer}>
					<p className={cx(styles.terms)}>
						{T.translate('vehicleDetails.bidsModal.legal.text', {
							terms: (
								<Link legacyBehavior as="/terms" href="/static_page">
									{
										// eslint-disable-next-line jsx-a11y/anchor-is-valid
										<a className="mw-link" onClick={triggerTermsLinkClicked} rel="noopener noreferrer" target="_blank">
											{T.translate('vehicleDetails.bidsModal.terms')}
										</a>
									}
								</Link>
							),
							type: T.translate(`vehicleDetails.bidsModal.legal.types.${modalType}`),
						})}
					</p>
					<div className={cx(styles.button)}>
						<Button
							fullWidth
							type="submit"
							{...applyCypressData(cypressIds.buttons.bidsModal)}
							disabled={this.state.sending || this.state.saleEnded}
							label={T.translate(`vehicleDetails.bidsModal.button.${modalType}`)}
							loading={this.state.sending}
						/>
					</div>
				</div>
			</form>
		);
	}
}

VehiclePurchaseModalContent.contextType = ToastContext;

VehiclePurchaseModalContent.propTypes = {
	bidCount: PropTypes.number,
	// remove with displayPrice feature toggle
	// eslint-disable-next-line react/forbid-prop-types
	features: PropTypes.object,
	isMobile: PropTypes.bool,
	modalType: PropTypes.oneOf(Object.values(VEHICLE_PURCHASE_MODAL_TYPES)),
	offsetTop: PropTypes.number,
	onMount: PropTypes.func,
	permits: proPropTypes.vehiclesPermitsChecker.isRequired,
	saleEnds: PropTypes.string,
	userPartners: PropTypes.arrayOf(PropTypes.string),
	value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	vehicle: proPropTypes.vehicleDetails.isRequired,
};

export { VehiclePurchaseModalContent as VehiclePurchaseModalContentWithoutPermitsChecker };
// remove withFeaturesContext with displayPrice feature toggle
export default withFeaturesContext(WithVehiclePermitsChecker()(VehiclePurchaseModalContent));
